import { Component, Suspense } from 'react';
import PropTypes from 'prop-types';

import FolderIcon from 'dibs-icons/exports/Folder';
import HeartIcon from 'dibs-icons/exports/HeartFilled';
import { HeartWrapperLazy } from '../../../global/HeartWrapperLazy';
import { FolderWrapperLazy } from '../../../global/FolderWrapperLazy';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import {
    FORCE_FAVORITE_ITEM_ID,
    FORCE_FOLDER_ITEM_ID,
    setForceFavoriteItemId,
    getForceFavoriteItemId,
    clearForceFavoriteItemId,
} from './localStorageHelpers';

// styles
import styles from './FavoriteIcons.scss';

// helpers
import {
    locations,
    authLabels,
    folderTrackingHooks,
    trackHeartFavorited,
    trackHeartUnfavorited,
} from '../../../utils/tracking/shared/favoritesTracking';
import { loadAddToFolderModalWrapper } from '../../../global/loadAddToFolderModalWrapper';
import classNames from 'classnames';
import {
    SAVE_ITEM_FLOW,
    SAVE_ITEM_TO_FOLDER_FLOW,
    sharedLoginCheck,
} from '../../../authentication/sharedLoginCheck';
export class FavoritesItem extends Component {
    constructor(props) {
        super(props);

        this.showAuthModal = this.showAuthModal.bind(this);
        this.getFolderModalComponent = this.getFolderModalComponent.bind(this);
        this.onAddItemSuccess = this.onAddItemSuccess.bind(this);
        this.onFavorited = this.onFavorited.bind(this);

        this.folderTrackingHooks = this.getFolderTrackingHooks(props);

        this.state = {
            openAddToFolderModal: false,
        };
    }

    componentDidMount() {
        const shouldOpenAddToFolderModal =
            getForceFavoriteItemId(FORCE_FOLDER_ITEM_ID) === this.props.itemId;

        if (shouldOpenAddToFolderModal) {
            this.setState({ openAddToFolderModal: true });
            clearForceFavoriteItemId(FORCE_FOLDER_ITEM_ID);
        }
    }

    onAddItemSuccess() {
        if (authModalLoader.isPaused()) {
            authModalLoader.resume();
        }
    }

    getFolderTrackingHooks(props) {
        let hooks = folderTrackingHooks;

        if (props.location) {
            hooks = Object.keys(folderTrackingHooks).reduce((map, current) => {
                map[current] = folderTrackingHooks[current].bind(null, props.location);
                return map;
            }, {});
        }

        return hooks;
    }

    showAuthModal({ flow, onSuccess, onError, shouldPauseAuthFlow } = {}) {
        const { itemImage, itemTitle } = this.props;

        sharedLoginCheck({
            onSuccess,
            onError,
            authOptions: {
                flow,
                shouldPauseAuthFlow,
                ga: { label: authLabels[this.props.location] },
                additionalProps: { item: { firstPhotoWebPath: itemImage, title: itemTitle } },
            },
        });
    }

    getFolderModalComponent() {
        return new Promise((resolve, reject) => {
            this.showAuthModal({
                flow: SAVE_ITEM_TO_FOLDER_FLOW,
                shouldPauseAuthFlow: state => state.step === 0,
                onSuccess: ({ userId, isVerifiedTrade }) => {
                    if (isVerifiedTrade) {
                        // after trade user logs in, page is reloaded, so there's no point in showing modal
                        setForceFavoriteItemId(FORCE_FOLDER_ITEM_ID, this.props.itemId);
                        return resolve();
                    }
                    return loadAddToFolderModalWrapper({ userId }).then(resolve);
                },
                onError: reject,
            });
        });
    }

    renderHeart() {
        if (this.props.disable) {
            return <HeartIcon className={styles.icon} />;
        }

        return (
            <HeartWrapperLazy
                load
                itemId={this.props.itemId}
                userId={this.props.userId}
                viewer={this.props.viewer}
                authModalShow={cb =>
                    this.showAuthModal({
                        flow: SAVE_ITEM_FLOW,
                        onSuccess: ({ isVerifiedTrade }) => {
                            if (isVerifiedTrade) {
                                setForceFavoriteItemId(FORCE_FAVORITE_ITEM_ID, this.props.itemId);
                            }
                            cb();
                        },
                    })
                }
                onFavorited={this.onFavorited}
                onUnFavorited={() => trackHeartUnfavorited(this.props.location)}
                className={this.props.heartIconClass}
                buttonClass={styles.heartButton}
                type={this.props.type}
                isStrokeWidthHeavy={this.props.isStrokeWidthHeavy}
            />
        );
    }

    onFavorited() {
        trackHeartFavorited(this.props.location, this.props.itemId);
    }

    renderFolder() {
        if (this.props.disable) {
            return <FolderIcon className={styles.icon} />;
        }

        return (
            <FolderWrapperLazy
                load
                userId={this.props.userId}
                userIds={this.props.userIds}
                primaryItemPk={this.props.itemId}
                selectedItemIds={this.props.itemIds}
                singlePortfolioItem={this.props.singlePortfolioItem}
                loadPortfolioItemData={this.props.loadPortfolioItemData}
                viewer={this.props.viewer}
                environment={this.props.environment}
                trackingHooks={this.folderTrackingHooks}
                getModalComponent={this.getFolderModalComponent}
                additionalIconClass={this.props.folderIconClass}
                onAddItemSuccess={this.onAddItemSuccess}
                shouldFillInFolder={!!this.props.userId}
                type={this.props.type}
                isStrokeWidthHeavy={this.props.isStrokeWidthHeavy}
                openOnRender={this.state.openAddToFolderModal}
            />
        );
    }

    render() {
        const {
            fetchHeart,
            fetchFolder,
            heartIconWrapperClass,
            folderIconWrapperClass,
            wrapperClass,
            disable,
        } = this.props;

        if (disable) {
            return null;
        }

        return (
            <div className={classNames(styles.wrap, wrapperClass)}>
                <Suspense fallback="">
                    {fetchHeart && (
                        <div
                            className={classNames(styles.iconWrap, heartIconWrapperClass)}
                            data-tn="item-save-to-portfolio"
                        >
                            {this.renderHeart()}
                        </div>
                    )}
                    {fetchFolder && (
                        <div
                            className={classNames(styles.iconWrap, folderIconWrapperClass)}
                            data-tn="item-save-to-folder"
                        >
                            {this.renderFolder()}
                        </div>
                    )}
                </Suspense>
            </div>
        );
    }
}

FavoritesItem.defaultProps = {
    fetchHeart: true,
    fetchFolder: true,
};

FavoritesItem.propTypes = {
    itemId: PropTypes.string.isRequired,
    itemImage: PropTypes.string,
    itemTitle: PropTypes.string,
    location: PropTypes.oneOf([
        locations.SIDEBAR,
        locations.SELLER_GRID,
        locations.SIMILAR_ITEM_GRID,
        locations.VISUALLY_SIMILAR_ITEM_GRID,
        locations.SEARCH_BROWSE,
        locations.ZOOM_MODAL,
        locations.VIEW_IN_ROOM,
    ]).isRequired,

    // passed from FavoritesProvider
    disable: PropTypes.bool,
    isVerifiedTrade: PropTypes.bool,
    userId: PropTypes.string,
    userIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    environment: PropTypes.object.isRequired,
    singlePortfolioItem: PropTypes.bool.isRequired,
    loadPortfolioItemData: PropTypes.bool.isRequired,
    viewer: PropTypes.object,
    fetchHeart: PropTypes.bool.isRequired,
    fetchFolder: PropTypes.bool.isRequired,
    heartIconClass: PropTypes.string,
    heartIconWrapperClass: PropTypes.string,
    folderIconClass: PropTypes.string,
    folderIconWrapperClass: PropTypes.string,
    wrapperClass: PropTypes.string,
    showTooltipOnFirstClick: PropTypes.bool,
    type: PropTypes.oneOf(['outline', 'button']),
    isStrokeWidthHeavy: PropTypes.bool,
};
